import { log } from '@hungryroot/o11y';
import { type OrderV2 } from '@hungryroot/rest';
import { analytics } from '../segment';
import { getReferralSessionStorage } from './components/Referral/getReferralSessionStorage';
import { getUtmSessionStorage } from './components/Utm/getUtmSessionStorage';

export type SegmentCompletedRegistrationMethod =
  | 'email'
  | 'facebook'
  | 'apple'
  | 'google';

const APP_DATA = {
  context: {
    app: {
      name: 'Hungryroot Web',
      namespace: 'hungryroot.com',
    },
  },
};

export function funnelAnalytics() {
  const utmData = getUtmSessionStorage();
  const referralData = getReferralSessionStorage();

  const defaultData = {
    ...APP_DATA,
    campaign: utmData,
  };

  // Additive cookbook-referral attribution (Exp 242). Always emitted so the
  // event schema is stable; values default to null when there is no referral.
  const referralProperties = {
    referral_source: referralData?.referral_source ?? null,
    referral_cookbook_user_id: referralData?.referral_cookbook_user_id ?? null,
    referral_link_id: referralData?.referral_link_id ?? null,
  };

  return {
    completedRegistration: (
      customer: {
        id: number;
        email: string;
      },
      method: 'email' | 'facebook' | 'apple' | 'google'
    ) => {
      const { id: user_id, email } = customer;

      const properties = {
        ...defaultData,
        email,
        method,
        ...referralProperties,
      };

      analytics.track('Completed Registration', properties);
      // Also emit the canonical cross-platform event name. Both fire for now;
      // `Completed Registration` stays until reporting migrates to
      // `user_registered`. Identical payload so the two never diverge.
      analytics.track('user_registered', properties);

      const logProperties = {
        ...defaultData,
        user_id,
        method,
        ...referralProperties,
      };

      log('Completed Registration', logProperties);
      log('user_registered', logProperties);
    },
    checkoutStarted: (customerId: number) => {
      analytics.track('Checkout Started', {
        ...defaultData,
      });

      log('Checkout Started', {
        ...defaultData,
        user_id: customerId,
      });
    },
    orderCompleted: (
      user_id: number,
      order: Pick<
        OrderV2,
        'id' | 'total' | 'shipping_cost' | 'tax' | 'discount' | 'promo_code_id'
      >
    ) => {
      const { id, total, shipping_cost, tax, discount, promo_code_id } = order;

      const orderData = {
        checkout_id: id,
        order_id: id,
        shipping: shipping_cost,
        coupon: promo_code_id,
        currency: 'USD',
        total,
        tax,
        discount,
      };

      analytics.track('Order Completed', {
        ...defaultData,
        ...orderData,
      });

      log('Order Completed', {
        ...defaultData,
        ...orderData,
        user_id,
      });
    },
  };
}
